import {Component, OnInit} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Nominee, NomineeGuardian, Relation} from "../../model/nominee.model";
import {NomineeService} from "../../service/nominee.service";
import {Keys, StorageService} from "../../service/storage.service";
import {FormGroupUtil} from "../../utils/FormGroupUtil";
import {isEmpty} from "lodash";
import {Logger} from "../../utils/Logger";

@Component({
    selector: 'app-nominee-form',
    templateUrl: './nominee-form.component.html',
    styleUrls: []
})

export class NomineeFormComponent implements OnInit {

    nomineeForm: FormGroup;
    nomineeGuardianForm: FormGroup;
    prefilledNominee: Nominee;

    isMinor: any;
    termsChecked: boolean;
    termsNotCheckedError: boolean;

    public readonly relations = [
        {value: Relation.CHILD, displayName: 'Child'},
        {value: Relation.SIBLING, displayName: 'Sibling'},
        {value: Relation.SPOUSE, displayName: 'Spouse'},
        {value: Relation.PARENT, displayName: 'Parent'}
    ];
    private readonly nomineeService: NomineeService;
    private readonly formBuilder: FormBuilder;
    private readonly storageService: StorageService;

    constructor(formBuilder: FormBuilder, nomineeService: NomineeService, storageService: StorageService) {
        this.nomineeService = nomineeService;
        this.formBuilder = formBuilder;
        this.storageService = storageService;
    }

    get nomineeControls() {
        return this.nomineeForm.controls;
    }

    get nomineeGuardianControls() {
        return this.nomineeGuardianForm.controls;
    }

    async ngOnInit(): Promise<void> {
        await this.initializeForm();
    }

    public async initializeForm(): Promise<void> {
        this.nomineeForm = this.formBuilder.group({
            userId: this.formBuilder.control(null),
            name: this.formBuilder.control(null, [Validators.required]),
            relation: this.formBuilder.control(null, [Validators.required]),
            isMinor: this.formBuilder.control(null, [Validators.required]),
            emailId: this.formBuilder.control(null),
            phoneNumber: this.formBuilder.control(null),
            address: this.formBuilder.control(null),
        });
        this.nomineeGuardianForm = this.formBuilder.group({
            name: this.formBuilder.control(null),
            emailId: this.formBuilder.control(null),
            phoneNumber: this.formBuilder.control(null),
            address: this.formBuilder.control(null)
        });
        this.nomineeForm.patchValue({userId: this.storageService.getItem(Keys.PROFILE_ID)});
        try {
            await this.prefillNominee();
        } catch (e) {
            Logger.log(`An error occurred while getting nominee, ${JSON.stringify(e)}`);
        }
    }

    updateMinorSelection(event) {
        this.setIsMinor(event.value === 'Yes' ? true : event.value === 'No' ? false : null);
    }

    updateConsentCheckbox(event) {
        this.termsChecked = event.checked;
    }

    setIsMinor(isMinor: boolean) {
        this.isMinor = isMinor;
        this.isMinor ? this.setValidatorsForMinor() : this.setValidatorsForNonMinor();
    }

    setValidatorsForMinor(): void {
        this.nomineeControls.emailId.clearValidators();
        this.nomineeControls.phoneNumber.clearValidators();
        this.nomineeControls.address.clearValidators();
        this.clearNomineeContactFields();

        this.nomineeGuardianControls.phoneNumber.setValidators([Validators.required, Validators.pattern('\\d{10}')]);
        this.nomineeGuardianControls.name.setValidators([Validators.required]);
        this.nomineeGuardianControls.emailId.setValidators([Validators.required, Validators.email]);
        this.nomineeGuardianControls.address.setValidators([Validators.required]);

        this.nomineeForm.updateValueAndValidity();
        this.nomineeGuardianForm.updateValueAndValidity();
    }

    setValidatorsForNonMinor(): void {
        this.nomineeControls.emailId.setValidators([Validators.required, Validators.email]);
        this.nomineeControls.phoneNumber.setValidators([Validators.required, Validators.pattern('\\d{10}')]);
        this.nomineeControls.address.setValidators([Validators.required]);

        this.nomineeGuardianControls.phoneNumber.clearValidators();
        this.nomineeGuardianControls.name.clearValidators();
        this.nomineeGuardianControls.emailId.clearValidators();
        this.nomineeGuardianControls.address.clearValidators();

        this.nomineeForm.updateValueAndValidity();
        this.nomineeGuardianForm.updateValueAndValidity();
    }

    public isFormValid(): boolean {
        FormGroupUtil.validateFormGroup(this.nomineeForm);
        if (this.isMinor) {
            FormGroupUtil.validateFormGroup(this.nomineeGuardianForm);
            return this.nomineeForm.valid && this.nomineeGuardianForm.valid;
        }
        return this.nomineeForm.valid;
    }

    public mapFormToNominee(): Nominee {
        const nominee = this.nomineeForm.value as Nominee;
        nominee.nomineeGuardian = this.isMinor ? this.nomineeGuardianForm.value as NomineeGuardian : null;
        return {
            ...this.prefilledNominee,
            ...nominee
        };
    }

    public async prefillNominee(): Promise<void> {
        const userId = this.storageService.getItem(Keys.PROFILE_ID);
        const nominee = await this.nomineeService.findNomineeByUserId(userId);
        if (!nominee) {
            return;
        }
        const isNomineeMinor = !isEmpty(nominee.nomineeGuardian);
        this.setIsMinor(isNomineeMinor);
        this.nomineeForm.patchValue({
            name: nominee.name,
            relation: nominee.relation,
            isMinor: isNomineeMinor ? 'Yes' : 'No',
            phoneNumber: nominee.phoneNumber,
            emailId: nominee.emailId,
            address: nominee.address
        });
        if (isNomineeMinor) {
            this.nomineeGuardianForm.patchValue({
                name: nominee.nomineeGuardian.name,
                emailId: nominee.nomineeGuardian.emailId,
                phoneNumber: nominee.nomineeGuardian.phoneNumber,
                address: nominee.nomineeGuardian.address
            });
        }
        this.prefilledNominee = nominee;
    }

    shouldAskConsent(): boolean {
        return !this.prefilledNominee || this.nomineeForm.dirty || this.nomineeGuardianForm.dirty;
    }

    validateTermsCheck(): boolean {
        if (this.shouldAskConsent() && !this.termsChecked) {
            this.termsNotCheckedError = true;
            return false;
        }
        this.termsNotCheckedError = false;
        return true;
    }

    private clearNomineeContactFields(): void {
        this.nomineeControls.emailId.reset();
        this.nomineeControls.phoneNumber.reset();
        this.nomineeControls.address.reset();
    }

    public async createOrUpdate(nominee: Nominee): Promise<Nominee> {
        if (this.prefilledNominee) {
            return await this.nomineeService.update(nominee.id, nominee);
        } else {
            return await this.nomineeService.create(nominee);
        }
    }


    public async saveForm(): Promise<Nominee> {
        const nominee = this.mapFormToNominee();
        return await this.createOrUpdate(nominee);

    }
}
